import { FormEvent, useEffect, useState } from 'react';
import { portalApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface StaffUser {
  id: string; username: string; email: string | null; full_name: string;
  is_active: boolean; last_login_at: string | null;
  role_id: string; role_key: string; role_name: string; is_superadmin: boolean;
}
interface Role { id: string; key: string; name: string }

export default function Users() {
  const { can, user: me } = useAuth();
  const canWrite = can('portal_users', 'write');

  const [users, setUsers] = useState<StaffUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [newPass, setNewPass] = useState('');

  async function load() {
    try {
      const [u, r] = await Promise.all([
        portalApi.get<StaffUser[]>('/users'),
        portalApi.get<Role[]>('/roles'),
      ]);
      setUsers(u); setRoles(r);
    } catch (err: any) { setError(err.message); }
  }
  useEffect(() => { void load(); }, []);

  async function toggleActive(u: StaffUser) {
    if (u.is_active && !confirm(`Disable ${u.username}? They won't be able to log in until you re-enable the account.`)) return;
    setError(''); setNote('');
    try {
      await portalApi.put(`/users/${u.id}/active`, { isActive: !u.is_active });
      await load();
    } catch (err: any) { setError(err.message); }
  }

  async function resetPassword(u: StaffUser) {
    if (newPass.length < 12) { setError('New password must be at least 12 characters.'); return; }
    setError(''); setNote('');
    try {
      await portalApi.put(`/users/${u.id}/password`, { newPassword: newPass });
      setResetFor(null); setNewPass('');
      setNote(`Password reset for ${u.username} — they've been signed out everywhere.`);
    } catch (err: any) { setError(err.message); }
  }

  async function changeRole(u: StaffUser, roleId: string) {
    setError('');
    try {
      await portalApi.patch(`/users/${u.id}`, { roleId });
      await load();
    } catch (err: any) { setError(err.message); }
  }

  return (
    <>
      <header className="page-head">
        <h1>Users</h1>
        <p className="muted">Staff accounts for the admin portal.</p>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'Add user'}
          </button>
        )}
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note}</div>}
      {creating && <CreateUser roles={roles} onDone={() => { setCreating(false); void load(); }} />}

      <div className="card">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Username</th><th>Role</th><th>Status</th><th>Last login</th>{canWrite && <th />}</tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <tr key={u.id} className={u.is_active ? '' : 'row-muted'}>
                  <td>{u.full_name}{isSelf && <span className="tag">you</span>}</td>
                  <td className="mono">{u.username}</td>
                  <td>
                    {canWrite && !isSelf ? (
                      <select
                        value={u.role_id}
                        onChange={(e) => changeRole(u, e.target.value)}
                      >
                        {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    ) : (
                      <span>{u.role_name}</span>
                    )}
                  </td>
                  <td>
                    <span className={'pill ' + (u.is_active ? 'pill-ok' : 'pill-off')}>
                      {u.is_active ? 'active' : 'disabled'}
                    </span>
                  </td>
                  <td className="muted">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'never'}
                  </td>
                  {canWrite && (<td className="right">
                    {!isSelf && (resetFor === u.id ? (
                      <span className="row-actions">
                        <input type="password" autoFocus placeholder="New password (12+)"
                          value={newPass} onChange={(e) => setNewPass(e.target.value)}
                          style={{ width: 180 }} />
                        <button className="btn btn-sm btn-primary" onClick={() => resetPassword(u)}>Save</button>
                        <button className="btn btn-sm btn-ghost" onClick={() => { setResetFor(null); setNewPass(''); }}>Cancel</button>
                      </span>
                    ) : (
                      <span className="row-actions">
                        <button className="btn btn-sm" onClick={() => toggleActive(u)}>
                          {u.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button className="btn btn-sm" onClick={() => { setResetFor(u.id); setNewPass(''); setError(''); setNote(''); }}>
                          Reset password
                        </button>
                      </span>
                    ))}
                  </td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="muted small">
        Accounts are disabled, never deleted, so audit history keeps pointing at a real person.
      </p>
    </>
  );
}

function CreateUser({ roles, onDone }: { roles: Role[]; onDone: () => void }) {
  const [form, setForm] = useState({ username: '', fullName: '', email: '', password: '', roleId: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await portalApi.post('/users', {
        username: form.username,
        fullName: form.fullName,
        email: form.email || undefined,
        password: form.password,
        roleId: form.roleId || roles[0]?.id,
      });
      onDone();
    } catch (err: any) {
      const e2 = err as ApiError;
      setError(Array.isArray(e2.body?.message) ? e2.body.message.join(', ') : e2.message);
    } finally { setBusy(false); }
  }

  return (
    <form className="card form-grid" onSubmit={submit}>
      <h2 className="card-title">New user</h2>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="grid-2">
        <label className="field"><span>Full name</span>
          <input required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></label>
        <label className="field"><span>Username</span>
          <input required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></label>
        <label className="field"><span>Email (optional)</span>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="field"><span>Password (min 12 chars)</span>
          <input type="password" required minLength={12} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
        <label className="field"><span>Role</span>
          <select value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
            <option value="">Select a role…</option>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select></label>
      </div>
      <button className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create user'}</button>
    </form>
  );
}
